import { useCallback, useMemo } from 'react';
import {
  Editor,
  Element as SlateElement,
  Transforms,
  createEditor,
} from 'slate';
import { withHistory } from 'slate-history';
import { Editable, Slate, useSlate, withReact } from 'slate-react';
import { Button, Icon, Toolbar } from './components';

const LIST_TYPES = ['numbered-list', 'bulleted-list'];
const TEXT_ALIGN_TYPES = ['left', 'justify'];

const RichTextArea = ({ value, onChange, placeholder = '' }) => {
  const renderElement = useCallback((props) => <Element {...props} />, []);
  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);
  return (
    <Slate
      editor={editor}
      initialValue={
        value
          ? parseRTValue(value)
          : [
              {
                type: 'paragraph',
                children: [{ text: '' }],
              },
            ]
      }
      onChange={onChange}
    >
      <Toolbar>
        <MarkButton format="bold" icon="format_bold" title="Negrita" />
        <MarkButton format="italic" icon="format_italic" title="Cursiva" />
        <MarkButton
          format="underline"
          icon="format_underlined"
          title="Subrayado"
        />
        <BlockButton
          format="bulleted-list"
          icon="format_list_bulleted"
          title="Viñetas"
        />
        <BlockButton
          format="numbered-list"
          icon="format_list_numbered"
          title="Numeración"
        />
        <BlockButton
          format="left"
          icon="format_align_left"
          title="Alinear a la izquierda"
        />
        <BlockButton
          format="justify"
          icon="format_align_justify"
          title="Justificar"
        />
      </Toolbar>
      <Editable
        renderElement={renderElement}
        renderLeaf={renderLeaf}
        placeholder={placeholder}
        spellCheck
        autoFocus
        style={{
          minHeight: '150px',
          padding: '10px',
          border: '1px solid #ddd',
          borderRadius: '5px',
          marginTop: '10px',
          // fontFamily should not change as the templates use this one
          fontFamily: 'Trebuchet MS',
          // fontSize in template will be reset to smaller (9.5px), bigger here for readability
          fontSize: '14px',
        }}
        renderPlaceholder={({ attributes, children }) => (
          <span
            {...attributes}
            style={{
              // padding: '0 10px', // Ensures the placeholder has the same padding
              position: 'absolute', // Keeps it aligned correctly
              pointerEvents: 'none', // Prevents interaction issues
              color: 'gray',
            }}
          >
            {children}
          </span>
        )}
      />
    </Slate>
  );
};

// RichText Toolbar functions

const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(
    editor,
    format,
    isAlignType(format) ? 'align' : 'type',
  );
  const isList = isListType(format);
  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      isListType(n.type) &&
      !isAlignType(format),
    split: true,
  });
  let newProperties;
  if (isAlignType(format)) {
    newProperties = {
      align: isActive ? undefined : format,
    };
  } else {
    newProperties = {
      type: isActive ? 'paragraph' : isList ? 'list-item' : format,
    };
  }
  Transforms.setNodes(editor, newProperties);
  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};
const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);
  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};
const isBlockActive = (editor, format, blockType = 'type') => {
  const { selection } = editor;
  if (!selection) return false;
  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) => {
        if (!Editor.isEditor(n) && SlateElement.isElement(n)) {
          if (blockType === 'align' && isAlignElement(n)) {
            return n.align === format;
          }
          return n.type === format;
        }
        return false;
      },
    }),
  );
  return !!match;
};
const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};
const Element = ({ attributes, children, element }) => {
  const style = {};
  if (isAlignElement(element)) {
    style.textAlign = element.align;
  }
  switch (element.type) {
    case 'block-quote':
      return (
        <blockquote style={style} {...attributes}>
          {children}
        </blockquote>
      );
    case 'bulleted-list':
      return (
        <ul style={style} {...attributes}>
          {children}
        </ul>
      );
    case 'heading-one':
      return (
        <h1 style={style} {...attributes}>
          {children}
        </h1>
      );
    case 'heading-two':
      return (
        <h2 style={style} {...attributes}>
          {children}
        </h2>
      );
    case 'list-item':
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      );
    case 'numbered-list':
      return (
        <ol style={style} {...attributes}>
          {children}
        </ol>
      );
    default:
      return (
        <p style={style} {...attributes}>
          {children}
        </p>
      );
  }
};
const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }
  if (leaf.code) {
    children = <code>{children}</code>;
  }
  if (leaf.italic) {
    children = <em>{children}</em>;
  }
  if (leaf.underline) {
    children = <u>{children}</u>;
  }
  return <span {...attributes}>{children}</span>;
};
const BlockButton = ({ format, icon, title }) => {
  const editor = useSlate();
  return (
    <Button
      active={isBlockActive(
        editor,
        format,
        isAlignType(format) ? 'align' : 'type',
      )}
      onMouseDown={(event) => {
        event.preventDefault();
        toggleBlock(editor, format);
      }}
      title={title}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};
const MarkButton = ({ format, icon, title }) => {
  const editor = useSlate();
  return (
    <Button
      active={isMarkActive(editor, format)}
      onMouseDown={(event) => {
        event.preventDefault();
        toggleMark(editor, format);
      }}
      title={title}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};
const isAlignType = (format) => {
  return TEXT_ALIGN_TYPES.includes(format);
};
const isListType = (format) => {
  return LIST_TYPES.includes(format);
};
const isAlignElement = (element) => {
  return 'align' in element;
};

// RichTextArea Form validation
export function getRTRule() {
  return {
    validator: (_, value) => {
      // Check if the editor content is empty
      const isEmpty =
        !value ||
        value.length === 0 ||
        (value.length === 1 &&
          value[0].type === 'paragraph' &&
          value[0].children?.length === 1 &&
          value[0].children[0].text.trim() === '');

      return isEmpty
        ? Promise.reject(new Error('Este campo es requerido'))
        : Promise.resolve();
    },
  };
}

export function parseRTValue(v) {
  if (typeof v === 'string') return JSON.parse(v);
  return v;
}

export function formatRTValue(v) {
  return JSON.stringify(v);
}

export default RichTextArea;
